import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Upload, Trash2, Plus, Type, X, Save, Palette, Image as ImageIcon, Check, Eye, ToggleLeft, ToggleRight, AlertCircle, Columns } from 'lucide-react';
import { toast } from 'sonner';
import './TemplateEditor.css';

const API = process.env.REACT_APP_BACKEND_URL;

const POST_TYPES = [
  "Just Listed", "Featured", "Sneak Peek", "Price Reduction",
  "Sold", "Under Offer", "Collage", "Custom",
];

const SIZE_DEFS = [
  { key: "1:1", label: "Square", dims: "1080×1080" },
  { key: "4:5", label: "Portrait", dims: "1080×1350" },
  { key: "9:16", label: "Story", dims: "1080×1920" },
];

const DATA_SOURCES = [
  { value: "{town}", label: "Town" },
  { value: "{price}", label: "Price" },
  { value: "{address}", label: "Address" },
  { value: "{bedrooms}", label: "Bedrooms" },
  { value: "{post_type}", label: "Post Type Label" },
];

const FONT_OPTIONS = [
  "Playfair Display", "Open Sans", "Montserrat", "Poppins",
  "Lora", "Raleway", "DM Serif Text", "Cormorant Garamond",
];

const SAMPLE_DATA = {
  "{town}": "Woking",
  "{price}": "£650,000",
  "{address}": "123 High Street",
  "{bedrooms}": "4",
  "{post_type}": "Just Listed",
};


// ── Post Type Section with 3 size upload zones ──
function PostTypeSection({ postType, templates, logos, branchId, enabledSizes, onToggleSize, onUpload, onDelete, onEditRegions }) {
  const getTemplate = (sizeKey) => templates.find(t => t.post_type === postType && t.format === sizeKey);

  return (
    <div className="te-pt-section" data-testid={`te-pt-${postType.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="te-pt-sizes">
        {SIZE_DEFS.map(size => {
          const tpl = getTemplate(size.key);
          const enabled = enabledSizes.includes(size.key);
          return (
            <SizeSlot
              key={size.key}
              size={size}
              template={tpl}
              enabled={enabled}
              postType={postType}
              branchId={branchId}
              logos={logos}
              onToggle={() => onToggleSize(postType, size.key)}
              onUpload={onUpload}
              onDelete={onDelete}
              onEditRegions={onEditRegions}
            />
          );
        })}
      </div>
    </div>
  );
}


// ── Individual Size Upload/Preview Slot ──
function SizeSlot({ size, template, enabled, postType, branchId, logos, onToggle, onUpload, onDelete, onEditRegions }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(postType, size.key, file);
      toast.success(`${postType} ${size.label} uploaded`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!template) return;
    try {
      await onDelete(postType, size.key);
      toast.success(`${postType} ${size.label} deleted`);
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className={`te-size-slot ${!enabled ? 'te-size-disabled' : ''}`} data-testid={`te-size-${postType.replace(/\s+/g, '-').toLowerCase()}-${size.key.replace(':', 'x')}`}>
      <div className="te-size-header">
        <span className="te-size-label">{size.label}</span>
        <span className="te-size-dims">{size.dims}</span>
        <button className="te-size-toggle" onClick={onToggle} title={enabled ? 'Disable this size' : 'Enable this size'} data-testid={`te-toggle-${size.key.replace(':', 'x')}`}>
          {enabled ? <ToggleRight size={18} className="te-toggle-on" /> : <ToggleLeft size={18} className="te-toggle-off" />}
        </button>
      </div>

      {enabled && (
        <div className="te-size-body">
          {template ? (
            <div className="te-size-preview">
              <div className="te-size-thumb">
                <div className="te-preview-checker-sm" />
                {template.overlay_url && <img src={`${template.overlay_url}?v=${template.updated_at || Date.now()}`} alt={`${postType} ${size.label}`} />}
              </div>
              <div className="te-size-actions">
                <button className="te-size-edit" onClick={() => onEditRegions(template)} data-testid={`te-edit-${size.key.replace(':', 'x')}`}>
                  <Type size={12} /> Edit
                </button>
                <label className="te-size-replace">
                  <Upload size={12} /> Replace
                  <input type="file" accept=".png" hidden ref={fileRef} onChange={handleFile} />
                </label>
                <button className="te-size-delete" onClick={handleDelete} data-testid={`te-del-${size.key.replace(':', 'x')}`}>
                  <Trash2 size={12} />
                </button>
              </div>
              {template.logo_label && <span className="te-size-logo-badge">Logo: {template.logo_label}</span>}
            </div>
          ) : (
            <label className={`te-size-dropzone ${uploading ? 'te-uploading' : ''}`}>
              {uploading ? (
                <span className="te-uploading-text">Uploading...</span>
              ) : (
                <>
                  <Upload size={20} />
                  <span>Upload overlay PNG</span>
                </>
              )}
              <input type="file" accept=".png" hidden ref={fileRef} onChange={handleFile} disabled={uploading} />
            </label>
          )}
        </div>
      )}

      {!enabled && (
        <div className="te-size-off">
          <X size={14} />
          <span>Disabled</span>
        </div>
      )}
    </div>
  );
}


// ── Text Region Editor Panel ──
function RegionEditor({ template, logos, branchId, onSave, onClose }) {
  const [regions, setRegions] = useState(template.text_regions || []);
  const [logoLabel, setLogoLabel] = useState(template.logo_label || '');
  const [saving, setSaving] = useState(false);

  const dims = template.format === '9:16' ? { w: 1080, h: 1920 } : template.format === '1:1' ? { w: 1080, h: 1080 } : { w: 1080, h: 1350 };
  const previewScale = 300 / dims.w;
  const previewH = dims.h * previewScale;

  const addRegion = () => {
    setRegions(prev => [...prev, {
      text: "{town}", fontFamily: "Open Sans", fontSize: 24,
      color: "#FFFFFF", x: 50, y: 80, align: "center", type: "text",
    }]);
  };

  const updateRegion = (idx, field, value) => {
    setRegions(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const updated = prev.map((r, i) => i === idx ? { ...r, [field]: value } : r);
      // If changing Y on a split region, sync the partner
      if (field === 'y') {
        const current = updated[idx];
        if (current?.splitWith === 'next' && updated[idx + 1]) {
          updated[idx + 1] = { ...updated[idx + 1], y: value };
        }
        // Check if previous region splits with this one
        if (idx > 0 && updated[idx - 1]?.splitWith === 'next') {
          updated[idx - 1] = { ...updated[idx - 1], y: value };
        }
      }
      return updated;
    });
  };

  const removeRegion = (idx) => {
    setRegions(prev => {
      // If removing a region that's part of a split, unsplit first
      const copy = [...prev];
      if (copy[idx]?.splitWith === 'next' && copy[idx + 1]) {
        copy[idx + 1] = { ...copy[idx + 1] }; // partner is now standalone
      }
      if (idx > 0 && copy[idx - 1]?.splitWith === 'next') {
        copy[idx - 1] = { ...copy[idx - 1], splitWith: undefined };
      }
      return copy.filter((_, i) => i !== idx);
    });
  };

  const splitRegions = (idx) => {
    if (idx >= regions.length - 1) return;
    setRegions(prev => {
      const updated = [...prev];
      const sharedY = updated[idx].y;
      updated[idx] = { ...updated[idx], splitWith: 'next', x: 35, align: 'right' };
      updated[idx + 1] = { ...updated[idx + 1], y: sharedY, x: 65, align: 'left' };
      return updated;
    });
  };

  const unsplitRegion = (idx) => {
    setRegions(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], splitWith: undefined, x: 50, align: 'center' };
      if (updated[idx + 1]) {
        updated[idx + 1] = { ...updated[idx + 1], x: 50, align: 'center' };
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(template.post_type, template.format, regions, logoLabel);
      toast.success('Template updated');
      onClose();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Build region cards — group split pairs together
  const renderRegionCards = () => {
    const cards = [];
    let i = 0;
    while (i < regions.length) {
      const r = regions[i];
      if (r.splitWith === 'next' && i + 1 < regions.length) {
        const r2 = regions[i + 1];
        // Capture indices per-iteration so onChange closures don't share a mutable `i`
        const idx = i;
        const idx2 = i + 1;
        // Render a split pair card
        cards.push(
          <div key={idx} className="te-region-card te-region-split-card" data-testid={`te-region-split-${idx}`}>
            <div className="te-split-badge" data-testid={`te-split-badge-${idx}`}>
              <Columns size={11} />
              <span>Split Row</span>
              <button className="te-unsplit-btn" onClick={() => unsplitRegion(idx)} title="Unsplit" data-testid={`te-unsplit-${idx}`}>
                <X size={11} />
              </button>
            </div>
            <div className="te-split-pair">
              {/* Left region */}
              <div className="te-split-half">
                <div className="te-region-row">
                  <select value={r.text} onChange={e => updateRegion(idx, 'text', e.target.value)} className="te-select te-select-sm">
                    {DATA_SOURCES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                  <select value={r.fontFamily} onChange={e => updateRegion(idx, 'fontFamily', e.target.value)} className="te-select te-select-sm">
                    {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <input type="number" value={r.fontSize} onChange={e => updateRegion(idx, 'fontSize', parseInt(e.target.value) || 16)} className="te-input-sm te-input-num" />
                  <input type="color" value={r.color} onChange={e => updateRegion(idx, 'color', e.target.value)} className="te-color" />
                </div>
                <div className="te-region-row te-region-pos">
                  <label>X <input type="range" min="0" max="100" value={r.x} onChange={e => updateRegion(idx, 'x', Number(e.target.value))} /> <span>{r.x}%</span></label>
                </div>
              </div>
              <div className="te-split-divider" />
              {/* Right region */}
              <div className="te-split-half">
                <div className="te-region-row">
                  <select value={r2.text} onChange={e => updateRegion(idx2, 'text', e.target.value)} className="te-select te-select-sm">
                    {DATA_SOURCES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                  <select value={r2.fontFamily} onChange={e => updateRegion(idx2, 'fontFamily', e.target.value)} className="te-select te-select-sm">
                    {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <input type="number" value={r2.fontSize} onChange={e => updateRegion(idx2, 'fontSize', parseInt(e.target.value) || 16)} className="te-input-sm te-input-num" />
                  <input type="color" value={r2.color} onChange={e => updateRegion(idx2, 'color', e.target.value)} className="te-color" />
                </div>
                <div className="te-region-row te-region-pos">
                  <label>X <input type="range" min="0" max="100" value={r2.x} onChange={e => updateRegion(idx2, 'x', Number(e.target.value))} /> <span>{r2.x}%</span></label>
                </div>
              </div>
            </div>
            {/* Shared Y slider */}
            <div className="te-region-row te-region-pos te-split-shared-y">
              <label>Y (shared) <input type="range" min="0" max="100" value={r.y} onChange={e => updateRegion(idx, 'y', Number(e.target.value))} /> <span>{r.y}%</span></label>
            </div>
          </div>
        );
        i += 2;
      } else {
        // Regular standalone region — capture idx to avoid closure-over-mutable-i
        const idx = i;
        cards.push(
          <div key={idx} className="te-region-card" data-testid={`te-region-${idx}`}>
            <div className="te-region-row">
              <select value={r.text} onChange={e => updateRegion(idx, 'text', e.target.value)} className="te-select te-select-sm">
                {DATA_SOURCES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <select value={r.fontFamily} onChange={e => updateRegion(idx, 'fontFamily', e.target.value)} className="te-select te-select-sm">
                {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <input type="number" value={r.fontSize} onChange={e => updateRegion(idx, 'fontSize', parseInt(e.target.value) || 16)} className="te-input-sm te-input-num" />
              <input type="color" value={r.color} onChange={e => updateRegion(idx, 'color', e.target.value)} className="te-color" />
              <button className="te-delete-sm" onClick={() => removeRegion(idx)}><Trash2 size={12} /></button>
            </div>
            <div className="te-region-row te-region-pos">
              <label>X <input type="range" min="0" max="100" value={r.x} onChange={e => updateRegion(idx, 'x', Number(e.target.value))} /> <span>{r.x}%</span></label>
              <label>Y <input type="range" min="0" max="100" value={r.y} onChange={e => updateRegion(idx, 'y', Number(e.target.value))} /> <span>{r.y}%</span></label>
              <select value={r.align || 'center'} onChange={e => updateRegion(idx, 'align', e.target.value)} className="te-select te-select-xs">
                <option value="left">L</option>
                <option value="center">C</option>
                <option value="right">R</option>
              </select>
            </div>
          </div>
        );

        // Add "Split" button between this and next region (if next exists and isn't already split)
        const nextIdx = idx + 1;
        if (nextIdx < regions.length && !regions[nextIdx - 1]?.splitWith) {
          cards.push(
            <button
              key={`split-${idx}`}
              className="te-split-btn"
              onClick={() => splitRegions(idx)}
              title="Put these two regions on the same line"
              data-testid={`te-split-${idx}`}
            >
              <Columns size={12} />
              <span>Split Row</span>
            </button>
          );
        }

        i += 1;
      }
    }
    return cards;
  };

  return (
    <div className="te-region-editor" data-testid="te-region-editor">
      <div className="te-region-header-bar">
        <h3>Edit: {template.post_type} ({template.format})</h3>
        <button className="te-close-btn" onClick={onClose}><X size={16} /></button>
      </div>

      <div className="te-region-layout">
        {/* Left: Controls */}
        <div className="te-region-controls">
          <div className="te-form-group">
            <label>Logo</label>
            <select value={logoLabel} onChange={e => setLogoLabel(e.target.value)} className="te-select" data-testid="te-logo-select">
              <option value="">No Logo</option>
              {logos.map(l => <option key={l.label} value={l.label}>{l.label}</option>)}
            </select>
          </div>

          <div className="te-regions-header">
            <span>Text Regions ({regions.length})</span>
            <button className="te-add-region" onClick={addRegion} data-testid="te-add-text">
              <Plus size={13} /> Add
            </button>
          </div>

          {renderRegionCards()}

          <button className="te-save-btn te-save-main" onClick={handleSave} disabled={saving} data-testid="te-save-regions">
            <Save size={15} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Right: Live preview */}
        <div className="te-region-preview-col">
          <div className="te-preview" style={{ width: 300, height: previewH }} data-testid="te-preview">
            <div className="te-preview-checker" />
            <div className="te-preview-photo" />
            {template.overlay_url && <img src={template.overlay_url} alt="Overlay" className="te-preview-overlay" />}
            {regions.map((r, idx) => {
              const sampleText = SAMPLE_DATA[r.text] || r.text;
              return (
                <div
                  key={idx}
                  className="te-preview-text"
                  style={{
                    left: `${r.x}%`,
                    top: `${r.y}%`,
                    color: r.color,
                    fontSize: `${Math.max(8, r.fontSize * previewScale)}px`,
                    textAlign: r.align || 'center',
                    transform: r.align === 'center' ? 'translateX(-50%)' : r.align === 'right' ? 'translateX(-100%)' : 'none',
                  }}
                >
                  {sampleText}
                </div>
              );
            })}
          </div>
          <span className="te-preview-label">{dims.w}×{dims.h}</span>
        </div>
      </div>
    </div>
  );
}


// ── Main Template Editor ──
export default function TemplateEditor({ branch, onClose }) {
  const getBranchId = () => branch?._id || branch?.id;

  const [logos, setLogos] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeSection, setActiveSection] = useState('templates');
  const [loading, setLoading] = useState(false);

  // Config state
  const [enabledPostTypes, setEnabledPostTypes] = useState([...POST_TYPES]);
  const [disabledSizes, setDisabledSizes] = useState({});

  // Logo state
  const [logoLabel, setLogoLabel] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Region editor
  const [editingTemplate, setEditingTemplate] = useState(null);

  const loadData = useCallback(async () => {
    const bid = getBranchId();
    if (!bid) return;
    setLoading(true);
    try {
      const [logosRes, templatesRes, configRes] = await Promise.all([
        axios.get(`${API}/api/templates/logos/${bid}`),
        axios.get(`${API}/api/templates/list/${bid}`),
        axios.get(`${API}/api/templates/config/${bid}`),
      ]);
      setLogos(logosRes.data.logos || []);
      setTemplates(templatesRes.data.templates || []);
      if (configRes.data.enabled_post_types) {
        setEnabledPostTypes(configRes.data.enabled_post_types);
      }
      if (configRes.data.disabled_sizes) {
        setDisabledSizes(configRes.data.disabled_sizes);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [branch]);

  useEffect(() => { loadData(); }, [loadData]);

  // Save config to backend
  const saveConfig = async (newEnabled, newDisabled) => {
    const bid = getBranchId();
    try {
      await axios.put(`${API}/api/templates/config/${bid}`, {
        enabled_post_types: newEnabled,
        disabled_sizes: newDisabled,
      });
    } catch (err) {
      console.error('Config save error:', err);
    }
  };

  // Toggle post type
  const togglePostType = (postType) => {
    const next = enabledPostTypes.includes(postType)
      ? enabledPostTypes.filter(pt => pt !== postType)
      : [...enabledPostTypes, postType];
    setEnabledPostTypes(next);
    saveConfig(next, disabledSizes);
  };

  // Toggle size for a post type
  const toggleSize = (postType, sizeKey) => {
    const current = disabledSizes[postType] || [];
    const next = current.includes(sizeKey)
      ? current.filter(s => s !== sizeKey)
      : [...current, sizeKey];
    const updated = { ...disabledSizes, [postType]: next };
    if (updated[postType].length === 0) delete updated[postType];
    setDisabledSizes(updated);
    saveConfig(enabledPostTypes, updated);
  };

  // Get enabled sizes for a post type
  const getEnabledSizes = (postType) => {
    const disabled = disabledSizes[postType] || [];
    return SIZE_DEFS.map(s => s.key).filter(k => !disabled.includes(k));
  };

  // Upload template overlay
  const handleUploadTemplate = async (postType, format, file) => {
    const bid = getBranchId();
    const fd = new FormData();
    fd.append('file', file);
    fd.append('post_type', postType);
    fd.append('branch_id', bid);
    fd.append('format', format);
    fd.append('logo_label', '');
    fd.append('text_regions', '[]');

    // Check if already exists — use update endpoint
    const existing = templates.find(t => t.post_type === postType && t.format === format);
    if (existing) {
      await axios.put(`${API}/api/templates/update`, fd);
    } else {
      await axios.post(`${API}/api/templates/create`, fd);
    }
    await loadData();
  };

  // Delete template
  const handleDeleteTemplate = async (postType, format) => {
    const bid = getBranchId();
    await axios.delete(`${API}/api/templates/${bid}/${encodeURIComponent(postType)}/${encodeURIComponent(format)}`);
    await loadData();
  };

  // Save text regions + logo
  const handleSaveRegions = async (postType, format, regions, logoLbl) => {
    const bid = getBranchId();
    const fd = new FormData();
    fd.append('post_type', postType);
    fd.append('branch_id', bid);
    fd.append('format', format);
    fd.append('logo_label', logoLbl || '');
    fd.append('text_regions', JSON.stringify(regions));
    await axios.put(`${API}/api/templates/update`, fd);
    await loadData();
  };

  // Logo upload
  const handleUploadLogo = async (e) => {
    e.preventDefault();
    if (!logoFile || !logoLabel.trim()) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', logoFile);
      fd.append('label', logoLabel.trim());
      fd.append('branch_id', getBranchId());
      await axios.post(`${API}/api/templates/logos/upload`, fd);
      setLogoLabel('');
      setLogoFile(null);
      toast.success('Logo uploaded');
      await loadData();
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLogo = async (label) => {
    try {
      await axios.delete(`${API}/api/templates/logos/${getBranchId()}/${encodeURIComponent(label)}`);
      toast.success('Logo deleted');
      await loadData();
    } catch {
      toast.error('Delete failed');
    }
  };

  // Count uploaded templates
  const uploadedCount = templates.length;
  const totalPossible = enabledPostTypes.length * SIZE_DEFS.length;

  if (editingTemplate) {
    return (
      <div className="te-wrap" data-testid="template-editor">
        <RegionEditor
          template={editingTemplate}
          logos={logos}
          branchId={getBranchId()}
          onSave={handleSaveRegions}
          onClose={() => setEditingTemplate(null)}
        />
      </div>
    );
  }

  return (
    <div className="te-wrap" data-testid="template-editor">
      {/* Section toggle */}
      <div className="te-tabs">
        <button
          className={`te-tab ${activeSection === 'logos' ? 'te-tab-active' : ''}`}
          onClick={() => setActiveSection('logos')}
          data-testid="te-logos-tab"
        >
          <Palette size={15} /> Logo Assets
        </button>
        <button
          className={`te-tab ${activeSection === 'templates' ? 'te-tab-active' : ''}`}
          onClick={() => setActiveSection('templates')}
          data-testid="te-templates-tab"
        >
          <ImageIcon size={15} /> Templates
          {uploadedCount > 0 && <span className="te-tab-badge">{uploadedCount}</span>}
        </button>
      </div>

      {/* ── LOGOS SECTION ── */}
      {activeSection === 'logos' && (
        <div className="te-section" data-testid="te-logos-section">
          <h3 className="te-section-title">Logo Assets</h3>
          <p className="te-section-desc">Upload logo variants (dark/light/colour) for this branch.</p>

          <form className="te-logo-form" onSubmit={handleUploadLogo}>
            <input
              type="text"
              placeholder="Label (e.g. Dark Logo)"
              value={logoLabel}
              onChange={e => setLogoLabel(e.target.value)}
              className="te-input"
              data-testid="te-logo-label"
            />
            <label className="te-file-btn">
              <Upload size={14} />
              {logoFile ? logoFile.name : 'Choose File'}
              <input type="file" accept="image/*" hidden onChange={e => setLogoFile(e.target.files?.[0] || null)} data-testid="te-logo-file" />
            </label>
            <button type="submit" className="te-save-btn" disabled={uploading || !logoFile || !logoLabel.trim()} data-testid="te-logo-upload">
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </form>

          <div className="te-logo-grid">
            {logos.length === 0 && <p className="te-empty">No logos uploaded yet</p>}
            {logos.map((logo, i) => (
              <div key={i} className="te-logo-card" data-testid={`te-logo-${i}`}>
                <img src={logo.url} alt={logo.label} className="te-logo-img" />
                <div className="te-logo-info">
                  <span className="te-logo-label">{logo.label}</span>
                  <button className="te-delete-sm" onClick={() => handleDeleteLogo(logo.label)} title="Delete">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TEMPLATES SECTION ── */}
      {activeSection === 'templates' && (
        <div className="te-section" data-testid="te-templates-section">
          <div className="te-section-header-row">
            <div>
              <h3 className="te-section-title">Template Overlays</h3>
              <p className="te-section-desc">Toggle post types on/off. Upload transparent PNG overlays for each size.</p>
            </div>
            <span className="te-coverage-badge">{uploadedCount} / {totalPossible} uploaded</span>
          </div>

          <div className="te-post-types-list">
            {POST_TYPES.map(pt => {
              const enabled = enabledPostTypes.includes(pt);
              const ptTemplates = templates.filter(t => t.post_type === pt);
              return (
                <div key={pt} className={`te-pt-block ${!enabled ? 'te-pt-off' : ''}`} data-testid={`te-pt-block-${pt.replace(/\s+/g, '-').toLowerCase()}`}>
                  <div className="te-pt-header">
                    <button className="te-pt-toggle" onClick={() => togglePostType(pt)} data-testid={`te-pt-toggle-${pt.replace(/\s+/g, '-').toLowerCase()}`}>
                      {enabled ? <ToggleRight size={22} className="te-toggle-on" /> : <ToggleLeft size={22} className="te-toggle-off" />}
                    </button>
                    <span className="te-pt-name">{pt}</span>
                    {enabled && <span className="te-pt-count">{ptTemplates.length} / {getEnabledSizes(pt).length}</span>}
                  </div>

                  {enabled && (
                    <PostTypeSection
                      postType={pt}
                      templates={templates}
                      logos={logos}
                      branchId={getBranchId()}
                      enabledSizes={getEnabledSizes(pt)}
                      onToggleSize={toggleSize}
                      onUpload={handleUploadTemplate}
                      onDelete={handleDeleteTemplate}
                      onEditRegions={setEditingTemplate}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
